import { useId, useState, type FormEvent } from 'react';
import type { UsuarioSesion } from '@matias-asset-os/contracts';
import { esIdiomaSoportado, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Icono } from '../componentes/Iconos.js';

interface Props {
  idioma: Idioma;
  onEntrar: (usuario: UsuarioSesion) => void;
  onIdioma: (idioma: Idioma) => void;
  avisoDemo?: boolean;
}

/**
 * Pantalla de acceso a doble panel: a la izquierda la marca sobre fondo navy,
 * a la derecha el formulario. Es la primera impresión del producto, así que se
 * trata como tal.
 *
 * Cada campo lleva su etiqueta asociada —no un marcador de posición haciendo de
 * etiqueta—, porque el marcador desaparece al escribir y deja a quien usa un
 * lector de pantalla sin saber qué campo está rellenando.
 *
 * SE ENTRA CON CORREO Y CONTRASEÑA, Y EL NAVEGADOR LAS RELLENA. Los dos campos
 * viven en un mismo `<form>`, llevan `name` y llevan `autocomplete`
 * —`username` y `current-password`—, que es lo que el gestor de contraseñas
 * mira para ofrecer la clave guardada. Sin esas tres cosas el gestor no
 * reconoce el formulario y no ofrece nada.
 */
export function Login({ idioma, onEntrar, onIdioma, avisoDemo = false }: Props) {
  const idEmail = useId();
  const idPassword = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async (evento: FormEvent) => {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const { usuario } = await api.entrar(email, password);
      onEntrar(usuario);
    } catch (fallo: unknown) {
      setError(
        fallo instanceof ErrorApi ? t(idioma, fallo.mensajeClave) : t(idioma, 'error.servidor'),
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="acceso">
      <aside className="acceso-marca-panel">
        {/*
          EL LOGO DE VERDAD, NO UNA IMITACION TIPOGRAFICA.

          Antes eran dos lineas de texto con la letra del sistema, que se
          parecia al logo sin serlo: la casa de la marca no estaba y el espaciado
          cambiaba con la fuente que cargara. El fichero es el mismo que usa la
          empresa en todo lo demas, con el fondo recortado para que sirva sobre
          este azul y sobre cualquier otro.
        */}
        <img
          className="acceso-logo"
          src="/marca/soluxe-logo.png"
          width={308}
          height={105}
          alt="SOLUXE · Real Estate y Management"
        />

        <div className="acceso-lema">
          <span className="acceso-regla" aria-hidden="true" />
          <h1>{t(idioma, 'acceso.lema')}</h1>
          <p>{t(idioma, 'acceso.sub')}</p>
        </div>

        <svg className="acceso-casa" viewBox="0 0 320 260" aria-hidden="true">
          <path
            d="M40 250 L40 120 L160 30 L280 120 L280 250"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path d="M120 250 L120 170 L200 170 L200 250" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <rect x="140" y="95" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M160 12 L160 40" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>

        <p className="acceso-pie">© {new Date().getFullYear()} · Soluxe Estates</p>
      </aside>

      <main className="acceso-panel">
        <div className="acceso-idiomas" role="group" aria-label="Idioma">
          {(['es', 'en', 'de'] as const).map((codigo) => (
            <button
              key={codigo}
              type="button"
              aria-pressed={idioma === codigo}
              onClick={() => esIdiomaSoportado(codigo) && onIdioma(codigo)}
            >
              {codigo.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="acceso-formulario">
          <span className="acceso-eyebrow">{t(idioma, 'acceso.bienvenido')}</span>
          <h2>{t(idioma, 'acceso.entrar')}</h2>

          {avisoDemo && (
            <p className="aviso-demo">
              Entorno de desarrollo con <strong>datos ficticios</strong>. Ninguna información real.
            </p>
          )}

          <form className="formulario" onSubmit={(e) => void enviar(e)}>
            <div className="campo">
              <label htmlFor={idEmail}>{t(idioma, 'auth.email')}</label>
              <input
                id={idEmail}
                name="email"
                type="email"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                placeholder="nombre@suempresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="campo">
              <label htmlFor={idPassword}>{t(idioma, 'auth.password')}</label>
              <div className="campo-clave">
                <input
                  id={idPassword}
                  name="password"
                  type={verClave ? 'text' : 'password'}
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="ver-clave"
                  aria-pressed={verClave}
                  aria-label={t(idioma, 'auth.password')}
                  onClick={() => setVerClave((v) => !v)}
                >
                  <Icono nombre={verClave ? 'ojo-tachado' : 'ojo'} />
                </button>
              </div>
            </div>

            {error && (
              <p className="error" role="alert">
                {error}
              </p>
            )}

            <button type="submit" className="acceso-entrar" disabled={enviando}>
              {enviando ? t(idioma, 'app.cargando') : t(idioma, 'auth.entrar')}
            </button>
          </form>

        </div>
      </main>
    </div>
  );
}
